import React, { useState, useMemo } from 'react'
import { useToast } from 'containers/ToastManager'
import { ModalInjectedProps } from 'containers/ModalManager'
import { useFlag } from 'constants/flags'

// Constants
import { routes } from 'constants/routes'

// Components
import Button from 'components/Button'
import { Text } from 'components/Box'
import Toasts from 'components/Toasts'
import Modal from 'components/Modal'
import RadioButton from 'components/RadioButton'
import SelectDropdown from '../../components/SelectDropdown'
import { OtherWorkspacesMenu } from './OtherWorkspacesMenu/OtherWorkspacesMenu'

// Icons
import { ReactComponent as FolderClosed } from 'icons/folder-closed-16.svg'

// Styles
import {
  Bold,
  WarningWrapper,
  HeaderItem,
  DangerRadioButton,
  MovingTextWrapper,
  Item,
} from './MoveShareToProject.styles'

// GraphQL
import { useGetProjects } from '../../operations'
import { useMoveShareTo } from 'modules/shares/operations/useMoveShareTo'
import {
  ProjectFragment,
  PublicWorkspaceFragment,
  ShareListItemFragment,
  TransferShareMutationVariables,
  useGetWorkspacesQuery,
  WorkspaceMinimalFragment,
} from 'gqlTypes'

// Utils
import { isMemberWorkspace } from 'modules/workspace/utils'
import { TransferDocumentInfo } from './types'

type CurrentWorkspaceType =
  | ReturnType<typeof normalizePublicWorkspace>
  | WorkspaceMinimalFragment

const normalizePublicWorkspace = (publicWorkspace: PublicWorkspaceFragment) => {
  if (publicWorkspace.type === 'PERSONAL' && publicWorkspace.userIsOwner) {
    return {
      ...publicWorkspace,
      userRole: 'ADMIN' as const,
      customer: null,
    }
  }

  return {
    ...publicWorkspace,
    userRole: 'MEMBER' as const,
    customer: null,
  }
}

type MembershipsTransferOperation = TransferShareMutationVariables['membershipsOperation']
interface MoveShareToProjectProps extends ModalInjectedProps {
  share: Pick<ShareListItemFragment, 'identifier' | 'workspace' | 'name'>
  currentProject?: ProjectFragment | null
}

const MoveShareToProject: React.FC<MoveShareToProjectProps> = props => {
  const { share, hideModal, currentProject } = props
  const { showToast } = useToast()

  const isDocumentTransferOn = useFlag(
    'documents-transfer-to-another-workspace'
  )

  const [membershipsOperation, setMembershipsOperation] = useState('')
  const [selectedProject, setSelectedState] = useState<
    TransferDocumentInfo | undefined
  >(undefined)

  const workspaceId = share.workspace.identifier

  const { moveShareToProject, moveShareToWorkspace, loading } = useMoveShareTo({
    onWorkspaceTransferCompleted: data => {
      const { share: updatedShare } = data.transferShare
      showToast(
        getWorkspaceMoveCompletedToast(
          updatedShare.project,
          selectedProject?.workspace!
        )
      )
      hideModal()
    },
    onProjectTransferCompleted: project => {
      showToast(
        <>
          Document moved to &ldquo;
          <Toasts.Link
            to={routes.WORKSPACE_PROJECT.create({
              projectId: project.shortId,
              workspaceId,
            })}>
            {project.name}
          </Toasts.Link>
          &rdquo;
        </>
      )
      hideModal()
    },
  })

  const { projects, loading: loadingProjects } = useGetProjects({ workspaceId })

  const { data, loading: loadingWorkspaces } = useGetWorkspacesQuery({
    skip: !isDocumentTransferOn,
  })
  const workspaces = data?.me?.workspaces || []

  /* Get the currentWorkspace and all the other ones to show in the list */
  const [currentWorkspace, otherWorkspaces] = workspaces.reduce(
    ([currentWorkspace, workspaces], workspace) => {
      if (share.workspace.identifier === workspace.identifier) {
        return [workspace, workspaces]
      }

      /* We only show other workspaces where the user is a member and has edit permissions */
      if (
        isMemberWorkspace(workspace) &&
        (workspace.userCanEdit || workspace.userRole === 'ADMIN')
      ) {
        return [currentWorkspace, [...workspaces, workspace]]
      }

      return [currentWorkspace, workspaces]
    },
    [
      normalizePublicWorkspace(share.workspace) as CurrentWorkspaceType,
      [] as WorkspaceMinimalFragment[],
    ]
  )

  const projectsList = useMemo(() => {
    const projectItems = projects?.entries || []

    return projectItems.filter(
      ({ identifier }) => identifier !== currentProject?.identifier
    )
  }, [projects, currentProject])

  const handleTransferMembershipsOptionChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMembershipsOperation(e.target.value)
  }

  const submit = () => {
    if (!selectedProject?.workspace) {
      moveShareToProject({
        shareId: share.identifier,
        projectId: selectedProject!.project.identifier,
      })
      return
    }

    moveShareToWorkspace({
      shareIdentifier: share.identifier,
      projectIdentifier: selectedProject.project.identifier || undefined,
      workspaceIdentifier: selectedProject.workspace.identifier || '',
      membershipsOperation: membershipsOperation as MembershipsTransferOperation,
    })
  }

  let placeholder = 'Select a project...'
  const hasNoProjects = projectsList.length + otherWorkspaces.length === 0

  if (loadingProjects || loadingWorkspaces) {
    placeholder = 'Loading projects...'
  } else if (hasNoProjects) {
    placeholder = 'No available projects'
  }

  const projectValue = selectedProject?.project as ProjectFragment

  return (
    <Modal title="Move Document?" onCancel={hideModal}>
      <Modal.Body>
        Choose where you want to move “<Bold>{share.name}</Bold>”.
        <br />
        <br />
        <SelectDropdown
          placeholder={placeholder}
          disabled={loading || hasNoProjects}
          items={projectsList}
          value={selectedProject?.project}
          onChange={(project, path) => {
            const [
              label /* eslint-disable-line @typescript-eslint/no-unused-vars */,
              workspace,
            ] = path

            setSelectedState({ project, workspace })
          }}
          renderItem={(project, { active, setItem, a11yProps }) => (
            <Item
              active={active}
              text={project.name}
              icon={FolderClosed}
              onClick={() => setItem()}
              {...a11yProps}
            />
          )}
          renderValueText={value => {
            if (selectedProject?.workspace) {
              return `${selectedProject.workspace.name} / ${selectedProject.project.name}`
            }

            return projectValue.name || value.name
          }}
          headerItem={
            <>
              <HeaderItem>{share.workspace.name}</HeaderItem>
              {projectsList.length === 0 && (
                <Item text="No projects available" disabled />
              )}
            </>
          }
          footerItem={
            isDocumentTransferOn &&
            currentWorkspace?.userRole === 'ADMIN' && (
              <OtherWorkspacesMenu workspaces={otherWorkspaces} />
            )
          }
        />
        {currentProject?.type === 'PERSONAL_DRAFTS' &&
          selectedProject &&
          projectValue?.type !== 'PERSONAL_DRAFTS' && (
            <>
              <WarningWrapper>
                If you move the document here,{' '}
                <Bold>everybody in the Workspace</Bold> will be able to access
                it.
                <br />
                <br />
                Documents cannot be moved back to My Drafts.
              </WarningWrapper>
            </>
          )}
        {/* We only show this text when the destination workspace is different from the current one  */}
        {selectedProject?.workspace &&
          selectedProject.workspace.identifier !==
            share.workspace.identifier && (
            <MovingTextWrapper>
              <Text>
                You&apos;re moving the document to{' '}
                <Bold>{selectedProject.workspace.name}</Bold>. Do you want to
                keep sharing the document with previously invited people?
              </Text>
              <RadioButton
                name="transfer-option"
                label="Yes, share it with the same people"
                checked={membershipsOperation === 'TRANSFER_NON_SSO'}
                value="TRANSFER_NON_SSO"
                onChange={handleTransferMembershipsOptionChange}
              />
              <DangerRadioButton
                name="transfer-option"
                label="No, remove previously invited people"
                checked={membershipsOperation === 'DELETE_ALL'}
                value="DELETE_ALL"
                onChange={handleTransferMembershipsOptionChange}
              />
              {currentWorkspace?.customer?.ssoEnabled ? (
                <WarningWrapper>
                  People with a Single Sign-On (SSO) account won’t be
                  transferred.
                </WarningWrapper>
              ) : null}
            </MovingTextWrapper>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal}>Cancel</Button>
        <Button
          variant="primary"
          disabled={
            !selectedProject ||
            (selectedProject.workspace && !membershipsOperation)
          }
          loading={loading}
          onClick={() => submit()}>
          Move Document
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MoveShareToProject

function getWorkspaceMoveCompletedToast(
  project: ProjectFragment | null,
  workspace: WorkspaceMinimalFragment
) {
  if (project) {
    return (
      <span>
        Document moved to &ldquo;
        <Toasts.Link
          to={routes.WORKSPACE_PROJECT.create({
            projectId: project.identifier,
            workspaceId: workspace.identifier,
          })}>
          {project.name}
        </Toasts.Link>
        &rdquo; in &ldquo;
        <Toasts.Link
          to={routes.WORKSPACE_PROJECT.create({
            projectId: project.shortId,
            workspaceId: workspace.identifier,
          })}>
          {workspace.name}
        </Toasts.Link>
        &rdquo; Workspace
      </span>
    )
  }

  return (
    <span>
      Document moved to &ldquo;
      <Toasts.Link
        to={routes.WORKSPACE_SHARES.create({
          workspaceId: workspace.identifier,
        })}>
        {workspace.name}
      </Toasts.Link>
      &rdquo; Workspace
    </span>
  )
}
